import { ChangeDetectorRef, Component, Input, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { BooleanConverter, InputConverter, JSONConverter } from '../../../inputConverter';
import { AoiService } from '../../../services/aoi.service';
import { GisToolsService } from '../../../services/gis-tools.service';
import { Juris } from '../../../types';
import { BaseComponent } from '../../base.component';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-route',
    templateUrl: './route.component.html',
    styleUrls: ['../../sharedStyles/base.scss', './route.component.scss']
})
export class RouteComponent extends BaseComponent {
    counties: any[];
    stateRoutes: any[];
    filteredStateRoutes: any[];
    segments: any[];
    segmentBegin: string;
    segmentEnd: string;
    segmentBegins: any[];
    segmentEnds: any[];

    isActive: boolean;
    private _county: any;
    set county(county: any) {
        this._county = county;
        this.filterRoutes();
    }
    get county() {
        return this._county;
    }

    @Input()
    countySortField: string;

    @Input() showOffset: false;

    private _route: any;
    @Input()
    set route(route: any) {
        this._route = route;
        this.filterSegments(route);
    }
    get route() {
        return this._route;
    }

    private _segment: any;
    set segment(segment: any) {
        this._segment = segment;

        if (segment) {
            this.segmentBegin = segment.segmentNumber;
            this.segmentEnd = segment.segmentNumber;
            this.segmentEnds = this.segmentBegins.filter(s => s.segmentNumber >= this.segmentBegin);
        }
    }
    get segment() {
        return this._segment;
    }

    private _offset: any;
    @Input()
    set offset(offset: any) {
        this._offset = offset;
    }
    get offset() {
        return this._offset;
    }

    @Input() @InputConverter(BooleanConverter) jurisSort: boolean = false;

    _jurisValues: number[] = [Juris.State, Juris.Turnpike, Juris.NonStateFederalAid, Juris.TollBridge]; // default juris values
    @Input()
    @InputConverter(JSONConverter)
    set jurisValues(value: number[]) {
        if (typeof (value) === 'string') {
            //convert comma separated string into number array
            const stringArr = (value as string).split(',');
            value = stringArr.map(x => (parseInt(x, 10)));
        }

        if (!this.defaultJurisSet) {
            this.jurisOptions.splice(0, 1, { name: '', values: value });
            this.defaultJurisSet = true;
        }
        if (value !== this.jurisOptions[0].values) {
            //when not default, add sort text, and sort routes by juris(& then selected sort)
            this.jurisSortStr = 'Juris, then';
            this.sortJurisFirst = true;
        } else {
            this.jurisSortStr = '';
            this.sortJurisFirst = false;
        }

        this._jurisValues = value;
        this.filterRoutes();
    }
    get jurisValues() {
        return this._jurisValues;
    }

    defaultJurisSet: boolean = false;
    jurisOptions = [
        { name: '', values: [Juris.State, Juris.Turnpike, Juris.NonStateFederalAid, Juris.TollBridge] },
        { name: 'State', values: [Juris.State] },
        { name: 'Non-State Federal Aid Roads', values: [Juris.NonStateFederalAid] }
    ];
    jurisSortStr: '' | 'Juris, then' = '';
    sortJurisFirst: boolean = false;

    @Input() @InputConverter(BooleanConverter) showFullName: boolean = false;
    sortRouteBy: 'number' | 'name' = 'number';

    @Input() @InputConverter(BooleanConverter) hideRamps: boolean = false;

    constructor(
        _vcr: ViewContainerRef,
        private readonly gisToolsService: GisToolsService,
        private readonly aoiService: AoiService,
        private readonly cdr: ChangeDetectorRef
    ) {
        super(_vcr, true);
    }

    @Input()
    public activate = async () => {
        if (this.isActive) { return; }

        try {
            this.counties = await this.gisToolsService.getAllCounties('countyCode, name', this.countySortField);
            this.cdr.detectChanges();
        } catch (error) {
            this.readyProblem(error);
        } finally {
            this.isReady();
            this.isActive = true;
        }
    }

    @Input()
    public reset = () => {
        this._county = null;
        this._route = null;
        this._segment = null;
        this._offset = null;
        this.segmentBegin = null;
        this.segmentEnd = null;
    }

    async filterRoutes() {
        try {
            const propertyList = ['countyCode', 'stateRouteNumber'];
            if (this.showFullName) {
                propertyList.push('fullName');
            }
            const properties = propertyList.join(', ');
            if (this.jurisValues) {
                this.stateRoutes = await this.gisToolsService.getStateRoutesByCountyJuris(this.county.countyCode, this.jurisValues, `${properties}, juris`);
            } else {
                this.stateRoutes = await this.gisToolsService.getStateRoutesByCounty(this.county.countyCode, properties);
            }
            this.filteredStateRoutes = this.stateRoutes.filter(r => r.countyCode === this.county.countyCode);
            if (this.hideRamps) {
                //remove 8000 and 9000 state routes from the results
                this.filteredStateRoutes = this.filteredStateRoutes.filter(r => !(parseInt(r.stateRouteNumber, 10) >= 8000 && parseInt(r.stateRouteNumber, 10) <= 9999));
            }
            this.filteredStateRoutes = this.filteredStateRoutes.map(r => {
                r.displayText = r.stateRouteNumber;
                if (r.fullName) {
                    r.displayText += ` (${r.fullName})`;
                    if (r.displayText.length >= 100) {
                        r.displayText = `${r.displayText.substring(0, 100)}...`;
                    }
                }
                return r;
            });
            this.route = null;
            this.routeSortChange();
            this.filteredStateRoutes.unshift({name: '', stateRouteNumber: ''});
            this.cdr.detectChanges();
        } catch (error) {
            this.readyProblem(error);
        }
    }

    async filterSegments(route) {
        try {
            this.segments = await this.gisToolsService.getSegmentsByCountyAndRoute(this.county.countyCode, route.stateRouteNumber, 'countyCode, stateRouteNumber, segmentNumber');

            if (route) {
                this.segmentBegins = this.segments.filter(s => {
                    return s.countyCode === route.countyCode && s.stateRouteNumber === route.stateRouteNumber && s.stateRouteNumber === route.stateRouteNumber;
                });
            }

            this.segment = null;
            this.cdr.detectChanges();
        } catch (error) {
            this.readyProblem(error);
        }
    }

    @Input()
    public addResultToMap = () => {
        const searchCriteria: any = {
            type: 'route',
            title: `Route (${this.route.stateRouteNumber})`,
            stateRouteNumber: this.route.stateRouteNumber
        };
        if (this.route.countyCode) {
            searchCriteria.title += `County (${this.route.countyCode})`;
            searchCriteria.countyCode = this.route.countyCode;
        }
        if (this.segment) {
            searchCriteria.title = `${searchCriteria.title} ${this.segmentBegin}-${this.segmentEnd}`;
            searchCriteria.segBgn = this.segmentBegin;
            searchCriteria.segEnd = this.segmentEnd;
        }
        if (this.offset) {
            searchCriteria.title = `${searchCriteria.title} ${this.offset}`;
            searchCriteria.offset = this.offset;
        }

        this.aoiService.setSearchCriterias([searchCriteria]);
    }

    routeSortChange() {
        if (this.county) {
            //reorder strings then sort
            if (this.sortRouteBy === 'name') {
                this.filteredStateRoutes = this.filteredStateRoutes.map(r => {
                    r.displayText = `${r.fullName} (${r.stateRouteNumber})`;
                    if (r.displayText.length >= 100) {
                        r.displayText = `${r.displayText.substring(0, 100)}...`;
                    }
                    return r;
                });
            } else if (this.sortRouteBy === 'number') {
                this.filteredStateRoutes = this.filteredStateRoutes.map(r => {
                    r.displayText = r.stateRouteNumber;
                    if (r.fullName) {
                        r.displayText += ` (${r.fullName})`;
                        if (r.displayText.length >= 100) {
                            r.displayText = `${r.displayText.substring(0, 100)}...`;
                        }
                    }
                    return r;
                });
            }

            this.filteredStateRoutes.sort((a, b) => {
                if (this.sortJurisFirst) { //sort by juris first if enabled
                    const aJurisIndex = this.jurisValues.indexOf(parseInt(a.juris, 10));
                    const bJurisIndex = this.jurisValues.indexOf(parseInt(b.juris, 10));

                    if (aJurisIndex > bJurisIndex) {
                        return 1;
                    } else if (aJurisIndex < bJurisIndex) {
                        return -1;
                    }
                }
                const aStr = a.displayText;
                const bStr = b.displayText;
                if (aStr.localeCompare(bStr) < 0) {
                    return -1;
                } else if (aStr.localeCompare(bStr) > 0) {
                    return 1;
                }
                return 0;
            });
        }
    }
}
